import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { invoiceService } from "@/services/invoiceService";
import { InvoiceType } from "@/types/db/invoices";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useNavigation } from "@react-navigation/native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Table, ColumnDef, EditableOption } from "@/components/NewTable";
import { theme } from "@/constants/themes";
import { formatCurrency as formatCurrencyLib } from "@/lib/format/currency";

// Status color mapping
const STATUS_COLORS: Record<string, keyof Pick<theme, "complete" | "pending" | "attention" | "neutral" | "todo">> = {
  DRAFT: "todo",
  SENT: "pending",
  PAID: "complete",
  OVERDUE: "attention",
  CANCELLED: "neutral",
};

export function RelatedInvoicesScreen() {
  const { colors } = useTheme();
  const { formatDate } = useDateTime();
  const { displayNames } = useAuth();
  const { invoices: invoicePermissions } = usePermissions();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const navigation = useNavigation();
  const { navigateToEntity } = useNavigationGuard();
  const [invoices, setInvoices] = useState<InvoiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");

  // Set up header with search
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <ThemedText style={styles.headerTitle}>{displayNames?.invoices?.plural || "Invoices"}</ThemedText>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
    });
  }, [navigation, searchValue, colors, displayNames?.invoices?.plural]);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!currentEntity?.currentId || !currentEntity?.currentPanel) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let fetchedInvoices: InvoiceType[] = [];

        switch (currentEntity.currentPanel) {
          case "project":
            fetchedInvoices = await invoiceService.getInvoicesByProjectId(currentEntity.currentId);
            break;
          case "client":
            fetchedInvoices = await invoiceService.getInvoicesByClientId(currentEntity.currentId);
            break;
          default:
            console.warn(`No invoice relationship defined for entity type: ${currentEntity.currentPanel}`);
        }

        setInvoices(fetchedInvoices);
      } catch (err: any) {
        console.error("Failed to fetch related invoices:", err);
        setError(err.message || "Failed to load invoices");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [currentEntity?.currentId, currentEntity?.currentPanel]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getStatusColor = (status?: string) => {
    const colorKey = STATUS_COLORS[status?.toUpperCase() || "DRAFT"] || "todo";
    return colors[colorKey].border;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const formatCurrency = (amount?: string | number | null) => formatCurrencyLib(amount, { blank: "—" });

  // Status options for editable picker
  const statusOptions: EditableOption[] = useMemo(
    () => [
      { value: "DRAFT", label: "Draft", color: colors.todo.border },
      { value: "SENT", label: "Sent", color: colors.pending.border },
      { value: "PAID", label: "Paid", color: colors.complete.border },
      { value: "OVERDUE", label: "Overdue", color: colors.attention.border },
      { value: "CANCELLED", label: "Cancelled", color: colors.neutral.border },
    ],
    [colors],
  );

  // Handle edit callback
  const handleEdit = useCallback(async (row: InvoiceType, key: string, newValue: string) => {
    if (!row.id) return;

    try {
      await invoiceService.updateInvoice(row.id, { [key]: newValue });
      setInvoices((prev) => prev.map((inv) => (inv.id === row.id ? { ...inv, [key]: newValue } : inv)));
    } catch (err) {
      console.error("Failed to update invoice:", err);
    }
  }, []);

  // Handle delete callback
  const handleDelete = useCallback(async (row: InvoiceType) => {
    if (!row.id) return;

    try {
      await invoiceService.deleteInvoicesByIds([row.id]);
      setInvoices((prev) => prev.filter((inv) => inv.id !== row.id));
    } catch (err) {
      console.error("Failed to delete invoice:", err);
    }
  }, []);

  const handleInvoicePress = useCallback(
    (invoice: InvoiceType) => {
      // Save current entity to navigation history before navigating away
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }

      // Navigate to the invoice
      navigateToEntity("invoice", invoice.id!);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  const handleProjectPress = useCallback(
    (invoice: InvoiceType) => {
      if (!invoice.project?.id) return;
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      navigateToEntity("project", invoice.project.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  // Define columns for the table
  const columns: ColumnDef<InvoiceType>[] = useMemo(() => {
    const cols: ColumnDef<InvoiceType>[] = [];

    // Invoice number column - clickable for navigation
    cols.push({
      key: "invoice_number",
      header: "Number",
      flex: 1,
      minWidth: 100,
      accessor: (row: any) => row.meta_data?.invoice_number || row.invoice_number || "—",
      onPress: (row) => handleInvoicePress(row),
    });

    // Status column with editable picker
    cols.push({
      key: "status",
      header: "Status",
      width: 140,
      align: "center",
      searchable: true,
      searchValue: (_, row) => row.status || "DRAFT",
      editable: {
        type: "picker",
        options: statusOptions,
        placeholder: "Select status",
      },
      render: (value, row) => (
        <View style={[styles.statusPill, { backgroundColor: getStatusColor(row.status) }]}>
          <ThemedText style={styles.statusText}>{row.status?.toUpperCase() || "DRAFT"}</ThemedText>
        </View>
      ),
    });

    // Date column
    cols.push({
      key: "invoice_date",
      header: "Date",
      width: 100,
      searchable: false,
      accessor: (row: any) => row.meta_data?.invoice_date || row.invoice_date,
      render: (value) => <ThemedText style={styles.cellText}>{value ? formatDate(value) : "—"}</ThemedText>,
    });

    // PO# column
    cols.push({
      key: "purchase_order_number",
      header: "PO#",
      width: 140,
      accessor: (row: any) => row.meta_data?.purchase_order_number || row.purchase_order_number || "—",
    });

    // Amount column
    cols.push({
      key: "amount_due",
      header: "Amount",
      width: 140,
      align: "right",
      searchable: false,
      accessor: (row: any) => row.meta_data?.amount_due || row.amount_due,
      render: (value) => (
        <ThemedText style={styles.cellText}>{formatCurrency(value as string | number | null)}</ThemedText>
      ),
    });

    // Line items count column
    cols.push({
      key: "line_items",
      header: "Items",
      width: 80,
      align: "center",
      searchable: false,
      accessor: (row: any) => {
        const items = row.meta_data?.line_items || row.line_items;
        return Array.isArray(items) ? items.length : 0;
      },
      render: (value) => <ThemedText style={styles.cellText}>{value || 0}</ThemedText>,
    });

    // Project column (skip when viewing from project panel - redundant)
    if (currentEntity?.currentPanel !== "project") {
      cols.push({
        key: "project",
        header: displayNames?.projects?.name || "Project",
        flex: 1,
        minWidth: 120,
        accessor: (row: any) => row.project?.name || "—",
        onPress: (row) => handleProjectPress(row),
      });
    }

    return cols;
  }, [
    statusOptions,
    formatDate,
    formatCurrency,
    getStatusColor,
    handleInvoicePress,
    handleProjectPress,
    currentEntity?.currentPanel,
    displayNames?.projects?.name,
  ]);

  const invoicesName = displayNames?.invoices?.plural?.toLowerCase() || "invoices";

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="alert-circle" size={48} color={colors.red.text} />
          <ThemedText style={styles.emptyText}>Failed to load {invoicesName}</ThemedText>
          <ThemedText style={{ opacity: 0.6, fontSize: 12 }}>{error}</ThemedText>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ width: "auto", flexGrow: 1 }}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <Table<InvoiceType>
        data={invoices}
        columns={columns}
        keyExtractor={(item) => item.id || ""}
        loading={loading}
        loadingRowCount={8}
        onEdit={handleEdit}
        canEdit={() => invoicePermissions.update}
        onDelete={handleDelete}
        canDelete={() => invoicePermissions.delete}
        searchValue={searchValue}
        sortable
        defaultSort={{ key: "invoice_date", direction: "desc" }}
        emptyMessage={`No ${invoicesName} found`}
        emptyIcon="receipt"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "600",
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "600",
    color: "white",
  },
  cellText: {
    fontSize: 12,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "600",
    opacity: 0.6,
  },
});
